/**
 * CR-8613: a one-line key to the colours on a call-off grid.
 *
 * Users asked what the pink, yellow and blue rows meant — nothing on screen
 * said. Each grid passes its own entries, so the key lists exactly the colours
 * that grid can show, in the same classes the grid uses.
 *
 * @link WAREHOUSE_GRID_COLOUR_KEY for the New Call Off warehouse grid.
 * @link CallOffLinesGrid for the call-off's lines.
 */
import type { ReactNode } from 'react';

export interface ColourKeyEntry {
  /** Classes for the swatch — the same ones the grid paints with. */
  swatchClassName: string;
  /** What the colour means, in a few words. */
  label: string;
  /** Replaces the swatch, e.g. the Direct star. */
  icon?: ReactNode;
}

interface ColourKeyProps {
  entries: readonly ColourKeyEntry[];
  className?: string;
}

export default function ColourKey({ entries, className = '' }: ColourKeyProps) {
  if (entries.length === 0) return null;
  return (
    <div
      role="note"
      aria-label="Colour key"
      className={`flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground ${className}`}
    >
      <span className="font-medium text-foreground">Colour key:</span>
      {entries.map(entry => (
        <span key={entry.label} className="inline-flex items-center gap-1.5">
          {entry.icon ?? (
            <span
              aria-hidden="true"
              className={`inline-block h-3 w-5 rounded-sm border border-border ${entry.swatchClassName}`}
            />
          )}
          {entry.label}
        </span>
      ))}
    </div>
  );
}
